import type { PropsWithChildren } from 'react';
import ErrorPage from '../components/ErrorPage';
import { LoadingFallback } from '../components/QueryResults/LoadingFallback';
import { SingleQueryResults } from '../components/QueryResults/SingleQueryResults';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useEffectiveMembershipStatus } from '../hooks/useEffectiveMembershipStatus';
import { useWorkspaceMembership } from '../hooks/useWorkspaceMembership';
import { ApiError } from '../utils/httpError';

/** Statuses where the membership record is genuinely unavailable to this user. */
const MEMBERSHIP_DENIAL_STATUSES = [403, 404];

/**
 * A 403/404 means the membership genuinely isn't there for this user, so
 * not-found is the right answer. Any other failure (500, timeout, dropped
 * connection) is transient — surfacing it as not-found would tell a legitimate
 * current member the page doesn't exist and hide the retry affordance.
 */
export const isMembershipDenial = (error: unknown): boolean =>
  error instanceof ApiError &&
  error.status !== undefined &&
  MEMBERSHIP_DENIAL_STATUSES.includes(error.status);

/**
 * Route guard that only renders its children for current-member workspaces.
 * Non-current members see not-found (same outcome as a missing route).
 * Waits for the active workspace (and the membership record, for the users
 * allowed to read it) before deciding, so a fresh staff approve/revoke on the
 * membership service wins over a stale workspace `membershipStatus` cache.
 *
 * Prefer the `Require*Access` guards in `RequireRoyaltiesAccess` for the
 * royalties pages — they wrap this guard (outermost, so the workspace refresh
 * below still runs) and add the addon check the page needs.
 */
export const RequireCurrentMember = ({ children }: PropsWithChildren) => {
  const [workspace, activeWorkspaceQuery] = useActiveWorkspace();
  const {
    hasBlockingWorkspaceRefreshError,
    isEnabled: hasMembershipQuery,
    isResolving,
    query: membershipQuery,
    retryWorkspaceRefresh,
  } = useWorkspaceMembership(workspace);
  const { isCurrentMember } = useEffectiveMembershipStatus(
    workspace,
    membershipQuery.data,
  );

  // The record only ever refines the workspace's own status, so a failure can
  // block access only where the workspace doesn't already grant it. A cached
  // record means a previous fetch succeeded, so a refetch error is ignored too.
  const hasBlockingMembershipError =
    hasMembershipQuery &&
    membershipQuery.isError &&
    !membershipQuery.data &&
    !isCurrentMember;

  return (
    <SingleQueryResults query={activeWorkspaceQuery}>
      {() => {
        // Keep SingleQueryResults' loader up while membership / workspace
        // refresh settles — returning null would flash a blank after the
        // workspace query already resolved.
        if (isResolving) return <LoadingFallback />;

        if (hasBlockingWorkspaceRefreshError) {
          return <ErrorPage genericError onRetry={retryWorkspaceRefresh} />;
        }

        if (hasBlockingMembershipError) {
          return isMembershipDenial(membershipQuery.error) ? (
            <ErrorPage notFound />
          ) : (
            <ErrorPage genericError />
          );
        }

        return isCurrentMember ? <>{children}</> : <ErrorPage notFound />;
      }}
    </SingleQueryResults>
  );
};
